/**
 * The authoring assistant — one chat over the manuscript.
 *
 * Hosted two ways: a tab in the desktop SidePane, where it persists next to search, and a
 * drawer on mobile. Keeping the body separate is what lets the desktop version stop
 * disappearing every time the author looks at the text.
 *
 * Everything the agent does lands in one ordered timeline — prose, tool calls, proposals —
 * so a grep appears where it actually happened rather than in a pile above the answer.
 *
 * Conversation history lives in the agent session server-side, so the timeline accumulates
 * across turns rather than resetting on each question.
 */

import { Add, Delete, History, Send, Stop, Terminal } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import ChatProposalLink from './ChatProposalLink'
import ContentProposalReview, { type ApplyProposalAction } from './ContentProposalReview'
import MarkdownText from './MarkdownText'
import {
  type ConversationSummary,
  deleteConversation,
  listConversations,
  loadConversation,
  saveConversation,
  streamAssistantTurn,
} from '../../../services/assistantStream'
import { CONTENT_PROPOSALS_CHANGED } from '../../../services/contentProposals'
import { useContentProposals } from '../hooks/useContentProposals'

export interface AssistantChatProps {
  bookId: string
  /** The file the author is looking at, for context. */
  filename?: string
  /** A block inside the marked passage — the server preloads the surrounding text from it. */
  startBlockId?: string
  /** The marked passage, sent live so unsaved edits are included. */
  markedText?: string
  direction: 'ltr' | 'rtl'
  /** Navigate to a block and highlight it — the same path a search result takes. */
  onApplyProposal?: ApplyProposalAction
  onGoTo?: (filename: string, blockId: string, text: string) => void
}

/** One entry in the conversation, in the order it happened. */
type TimelineItem =
  | { kind: 'question'; text: string }
  | { kind: 'tool'; name: string; detail?: string }
  | { kind: 'toolResult'; text: string; isError?: boolean }
  | { kind: 'text'; text: string }
  | { kind: 'proposal'; proposalId: string }

/**
 * Format the API's monetary amount.
 *
 * The API reports minor units as an integer ("171" is $1.71), and how many minor units make
 * a major one is currency-dependent — JPY has none, USD has two. `Intl` knows the exponent
 * per currency, so ask it rather than assuming 100.
 */
function formatCost(minorUnits: number, currency: string): string {
  const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency })
  const digits = formatter.resolvedOptions().maximumFractionDigits ?? 2
  return formatter.format(minorUnits / 10 ** digits)
}

/** A tool call, kept deliberately quiet — it is a trace, not the content. */
/** A tool's output, shown under the call that produced it. */
const ToolResultRow: React.FC<{ text: string; isError?: boolean }> = ({ text, isError }) => (
  <Box
    dir="ltr"
    sx={{
      mt: -0.5,
      mb: 0.75,
      px: 1,
      py: 0.5,
      borderRadius: 1,
      border: 1,
      borderColor: isError ? 'error.main' : 'divider',
      borderTop: 0,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      bgcolor: 'action.hover',
      maxHeight: 180,
      overflowY: 'auto',
      textAlign: 'left',
    }}
  >
    <Typography
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.66rem',
        opacity: isError ? 0.9 : 0.5,
        color: isError ? 'error.main' : undefined,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Typography>
  </Box>
)

const ToolRow: React.FC<{ name: string; detail?: string; hasResult?: boolean }> = ({ name, detail, hasResult }) => (
  <Box
    dir="ltr"
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 0.75,
      px: 1,
      py: 0.5,
      mt: 0.75,
      mb: 0.75,
      borderRadius: 1,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'action.hover',
      textAlign: 'left',
      ...(hasResult && { mb: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }),
    }}
  >
    <Terminal sx={{ fontSize: 13, mt: '2px', opacity: 0.5, flexShrink: 0 }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.68rem', opacity: 0.85 }}>
        {name}
      </Typography>
      {detail && (
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.68rem',
            opacity: 0.55,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {detail}
        </Typography>
      )}
    </Box>
  </Box>
)

const AssistantChat: React.FC<AssistantChatProps> = ({
  bookId,
  filename,
  startBlockId,
  markedText,
  direction,
  onGoTo,
  onApplyProposal,
}) => {
  const [reviewing, setReviewing] = useState(false)
  const [conversationId, setConversationId] = useState<string>(() => crypto.randomUUID())
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null)
  const [question, setQuestion] = useState('')
  const [timeline, setTimeline] = useState<TimelineItem[]>([])
  const [status, setStatus] = useState('')
  const [usage, setUsage] = useState('')
  const [isRunning, setIsRunning] = useState(false)
  const { proposals, proposalError, proposalsLoaded } = useContentProposals(bookId, true, conversationId)
  const proposalById = new Map(proposals.map((proposal) => [proposal.id, proposal]))
  const linkedIds = new Set(timeline.flatMap((item) => (item.kind === 'proposal' ? [item.proposalId] : [])))
  const recoveredProposals = proposals.filter((proposal) => !linkedIds.has(proposal.id))
  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  const refreshConversations = useCallback(() => {
    void listConversations(bookId).then(setConversations)
  }, [bookId])

  useEffect(() => {
    refreshConversations()
  }, [refreshConversations])

  /** Start a fresh conversation — new id means a new agent session, so no context carries over. */
  const startNewChat = useCallback(() => {
    abortRef.current?.abort()
    setConversationId(crypto.randomUUID())
    setTimeline([])
    setUsage('')
    setStatus('')
  }, [])

  const openConversation = useCallback(
    async (id: string) => {
      setHistoryAnchor(null)
      const stored = await loadConversation(bookId, id)
      if (!stored) {
        toast.error('לא הצלחתי לטעון את השיחה')
        return
      }
      abortRef.current?.abort()
      setConversationId(id)
      setTimeline(stored.timeline as TimelineItem[])
      setUsage('')
    },
    [bookId],
  )

  const removeConversation = useCallback(
    async (id: string) => {
      await deleteConversation(bookId, id)
      refreshConversations()
      if (id === conversationId) {
        startNewChat()
      }
    },
    [bookId, conversationId, refreshConversations, startNewChat],
  )

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [timeline])

  const stop = useCallback(() => {
    abortRef.current?.abort()
    setStatus('עוצר…')
  }, [])

  const ask = useCallback(async () => {
    const trimmed = question.trim()
    if (!trimmed || isRunning) {
      return
    }

    const turnTimeline: TimelineItem[] = [...timeline, { kind: 'question', text: trimmed }]
    setTimeline([...turnTimeline])
    setQuestion('')
    setUsage('')
    setStatus('שולח…')
    setIsRunning(true)

    const controller = new AbortController()
    abortRef.current = controller
    const append = (item: TimelineItem) => {
      turnTimeline.push(item)
      if (abortRef.current === controller) {
        setTimeline([...turnTimeline])
      }
    }

    try {
      for await (const event of streamAssistantTurn(
        { bookId, conversationId, message: trimmed, filename, startBlockId, markedText },
        controller.signal,
      )) {
        if (event.type === 'status') {
          setStatus(event.message)
        } else if (event.type === 'tool') {
          append({ kind: 'tool', name: event.name, detail: event.detail })
          setStatus(event.name)
        } else if (event.type === 'toolResult') {
          append({ kind: 'toolResult', text: event.text, isError: event.isError })
        } else if (event.type === 'proposal') {
          if (!turnTimeline.some((item) => item.kind === 'proposal' && item.proposalId === event.proposal.id)) {
            append({ kind: 'proposal', proposalId: event.proposal.id })
          }
          window.dispatchEvent(new Event(CONTENT_PROPOSALS_CHANGED))
        } else if (event.type === 'delta') {
          const last = turnTimeline[turnTimeline.length - 1]
          if (last?.kind === 'text') {
            turnTimeline[turnTimeline.length - 1] = { kind: 'text', text: last.text + event.text }
            setTimeline([...turnTimeline])
          } else {
            append({ kind: 'text', text: event.text })
          }
          setStatus('')
        } else if (event.type === 'usage') {
          const parts = [
            event.costMinorUnits != null && event.currency
              ? formatCost(event.costMinorUnits, event.currency)
              : undefined,
            event.inputTokens != null ? `${event.inputTokens.toLocaleString()} in` : undefined,
            event.outputTokens != null ? `${event.outputTokens.toLocaleString()} out` : undefined,
            event.cacheReadTokens ? `${event.cacheReadTokens.toLocaleString()} cached` : undefined,
            event.cacheCreationTokens ? `${event.cacheCreationTokens.toLocaleString()} written` : undefined,
          ].filter(Boolean)
          setUsage(parts.join(' · '))
        } else if (event.type === 'error') {
          toast.error(event.message)
        }
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        toast.error(error instanceof Error ? error.message : 'Assistant request failed')
      }
    } finally {
      const persisted = turnTimeline.filter((item) => item.kind !== 'toolResult')
      await saveConversation(bookId, conversationId, trimmed.slice(0, 120), persisted)
      refreshConversations()
      if (abortRef.current === controller) {
        setStatus('')
        setIsRunning(false)
        abortRef.current = null
      }
    }
  }, [timeline, bookId, conversationId, filename, isRunning, markedText, question, refreshConversations, startBlockId])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Enter sends; Shift+Enter keeps the newline, matching every other chat surface.
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        void ask()
      }
    },
    [ask],
  )

  const chat = (
    <Box dir={direction} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ px: 0.75, py: 0.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tooltip title="שיחה חדשה">
          <IconButton size="small" onClick={startNewChat} disabled={isRunning} aria-label="שיחה חדשה">
            <Add fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="שיחות קודמות">
          <span>
            <IconButton
              size="small"
              onClick={(event) => setHistoryAnchor(event.currentTarget)}
              disabled={isRunning || conversations.length === 0}
              aria-label="שיחות קודמות"
            >
              <History fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Menu anchorEl={historyAnchor} open={!!historyAnchor} onClose={() => setHistoryAnchor(null)}>
        {conversations.map((conversation) => (
          <MenuItem
            key={conversation.conversationId}
            selected={conversation.conversationId === conversationId}
            onClick={() => void openConversation(conversation.conversationId)}
            sx={{ maxWidth: 360 }}
          >
            <ListItemText
              primary={conversation.title || '(ללא כותרת)'}
              secondary={new Date(conversation.updatedAt).toLocaleString()}
              primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
            <IconButton
              size="small"
              edge="end"
              onClick={(event) => {
                event.stopPropagation()
                void removeConversation(conversation.conversationId)
              }}
              aria-label="מחק שיחה"
            >
              <Delete sx={{ fontSize: 15 }} />
            </IconButton>
          </MenuItem>
        ))}
      </Menu>

      {markedText && (
        <Box sx={{ px: 1.25, pt: 1.25 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              maxHeight: 110,
              overflowY: 'auto',
              bgcolor: 'action.hover',
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
            }}
          >
            {markedText}
          </Paper>
        </Box>
      )}

      <Box ref={scrollRef} sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 1.25, py: 1.25 }}>
        {timeline.map((item, index) => {
          if (item.kind === 'question') {
            return (
              <Paper key={index} variant="outlined" sx={{ p: 1, my: 0.75, bgcolor: 'action.selected' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {item.text}
                </Typography>
              </Paper>
            )
          }

          if (item.kind === 'tool') {
            return (
              <ToolRow
                key={index}
                name={item.name}
                detail={item.detail}
                hasResult={timeline[index + 1]?.kind === 'toolResult'}
              />
            )
          }

          if (item.kind === 'toolResult') {
            return <ToolResultRow key={index} text={item.text} isError={item.isError} />
          }

          if (item.kind === 'text') {
            return (
              <Box key={index} sx={{ my: 0.5 }}>
                <MarkdownText>{item.text}</MarkdownText>
              </Box>
            )
          }

          const proposal = proposalById.get(item.proposalId)
          return proposal ? (
            <ChatProposalLink key={index} item={proposal} onGoTo={onGoTo} />
          ) : (
            <Typography key={index} variant="caption">
              {proposalsLoaded ? 'ההצעה אינה זמינה' : 'טוען הצעה…'}
            </Typography>
          )
        })}

        {proposalError && <Alert severity="warning">לא ניתן לעדכן את מצב ההצעות: {proposalError}</Alert>}
        {recoveredProposals.map((proposal) => (
          <ChatProposalLink key={proposal.id} item={proposal} onGoTo={onGoTo} />
        ))}

        {usage && !isRunning && (
          <Typography variant="caption" dir="ltr" sx={{ display: 'block', mt: 1, opacity: 0.6, textAlign: 'left' }}>
            session total: {usage}
          </Typography>
        )}
      </Box>

      <Stack spacing={1} sx={{ p: 1.25, borderTop: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          multiline
          maxRows={6}
          size="small"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="מה לשאול?"
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={isRunning ? <Stop /> : <Send />}
            onClick={() => (isRunning ? stop() : void ask())}
            disabled={!isRunning && !question.trim()}
          >
            {isRunning ? 'עצור' : 'שלח'}
          </Button>
          {status && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ opacity: 0.7, minWidth: 0 }}>
              <CircularProgress size={13} />
              <Typography variant="caption" noWrap>
                {status}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  )
  return (
    <Box dir={direction} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Stack direction="row" sx={{ p: 0.75, gap: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Button size="small" variant={reviewing ? 'text' : 'outlined'} onClick={() => setReviewing(false)}>
          שיחה
        </Button>
        <Button size="small" variant={reviewing ? 'outlined' : 'text'} onClick={() => setReviewing(true)}>
          הצעות לבדיקה
        </Button>
      </Stack>
      {reviewing ? <ContentProposalReview bookId={bookId} onGoTo={onGoTo} onApply={onApplyProposal} /> : chat}
    </Box>
  )
}

export default AssistantChat
